/**
 * Central registry for icon packs. Built-in packs are imported eagerly;
 * external manifest packs are loaded lazily at app start (see
 * `loadExternalPacks` below) and re-registered into the same map.
 *
 * UI consumes the registry via `useActiveIconPack()` (returns the pack chosen
 * in user settings, falling back to the default if the chosen one was
 * uninstalled or never loaded).
 */
import { create } from 'zustand';
import { useSettingsStore } from '@/stores/settingsStore';
import { lucideDefault } from './lucideDefault';
import { lucideMono } from './lucideMono';
import { packFromManifest } from './external';
import type { ExternalIconPackManifest, IconPack } from './types';

interface IconPackRegistryState {
  packs: Record<string, IconPack>;
  /** Order shown in the picker. Built-ins first, then external by name. */
  order: string[];
  registerExternal: (manifests: ExternalIconPackManifest[]) => void;
}

const BUILTIN_PACKS: IconPack[] = [lucideDefault, lucideMono];
export const DEFAULT_ICON_PACK_ID = lucideDefault.id;

export const useIconPackRegistry = create<IconPackRegistryState>((set) => ({
  packs: Object.fromEntries(BUILTIN_PACKS.map((p) => [p.id, p])),
  order: BUILTIN_PACKS.map((p) => p.id),

  registerExternal: (manifests) => {
    set((s) => {
      const next = { ...s.packs };
      const externalIds: string[] = [];
      for (const m of manifests) {
        if (!m.id || !m.name) continue;
        // Manifest-supplied id may collide with a built-in — disallow override
        // so users can't accidentally shadow the default and lock themselves
        // out. They can pick a unique id in their manifest.
        if (BUILTIN_PACKS.some((b) => b.id === m.id)) continue;
        next[m.id] = packFromManifest(m);
        externalIds.push(m.id);
      }
      // Sort external by display name for a stable picker order.
      externalIds.sort((a, b) => next[a].name.localeCompare(next[b].name));
      return {
        packs: next,
        order: [...BUILTIN_PACKS.map((p) => p.id), ...externalIds],
      };
    });
  },
}));

/** React hook returning the currently-active IconPack. */
export function useActiveIconPack(): IconPack {
  const packs = useIconPackRegistry((s) => s.packs);
  const id = useSettingsStore((s) => s.fileExplorer.iconPackId);
  return packs[id] ?? packs[DEFAULT_ICON_PACK_ID];
}
